import React from 'react';
import { displayStatus, evaluationPercentage } from '../../lib/evaluationIntegrity';
import StatusBadge from '../StatusBadge';
import { ArrowRight, Building2, CalendarDays, FolderOpen, MapPin } from 'lucide-react';
import SemaforoBadge from '../SemaforoBadge';

// #ClienteJerarquia — el dashboard del cliente colgaba TODO de las
// evaluaciones publicadas. Cuando un cliente tiene sus evals en borrador
// (caso real: OCESA, 3 proyectos + 2 eventos y 0 evaluaciones publicadas)
// la pantalla quedaba en ceros y parecía que la plataforma estaba vacía o
// rota. Este componente muestra la jerarquía real —Proyecto -> Eventos ->
// Evaluaciones publicadas— y explica cada nivel vacío en vez de dejar un
// cero mudo. Nunca recibe evals sin publicar: el filtro vive en el
// Dashboard (status === 'publicada') y el backend además lo fuerza.

// Fechas del backend vienen como ISO string y pueden ser null o basura;
// devolvemos null para que cada nivel decida su propio texto de respaldo.
function formatDate(raw) {
  if (!raw) return null;
  const d = new Date(raw);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
}

const plural = (n, singular, pluralWord) => `${n} ${n === 1 ? singular : pluralWord}`;

function EvaluationRow({ evaluation, onOpen }) {
  // La fecha de captura del levantador es la que le importa al cliente;
  // published_at solo como respaldo (mismo criterio que el Historial).
  const date = formatDate(evaluation.general_fields?.fecha || evaluation.published_at);
  const title =
    evaluation.name ||
    evaluation.general_fields?.instalacion ||
    evaluation.location_name ||
    'Evaluación de seguridad';

  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(evaluation.id)}
        className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-sm border border-slate-100 hover:border-soem-primary hover:bg-blue-50/40 transition-colors text-left"
        data-testid={`jerarquia-eval-${evaluation.id}`}
      >
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-soem-navy truncate">
            {title}
            <span className="ml-2 bg-slate-100 text-slate-500 text-xs px-1.5 py-0.5 rounded font-mono">
              v{evaluation.version}
            </span>
          </span>
          <span className="block text-xs text-slate-400 mt-0.5">{date || 'Sin fecha registrada'}</span>
        </span>
        <span className="flex items-center gap-2 flex-shrink-0">
          <SemaforoBadge semaforo={evaluation.semaforo} percentage={evaluationPercentage(evaluation)} size="sm"
          />
          <StatusBadge status={displayStatus(evaluation)} />
          <ArrowRight size={14} className="text-slate-300" />
        </span>
      </button>
    </li>
  );
}

function EvaluationList({ evaluations, onOpen }) {
  if (evaluations.length === 0) {
    // NO decir "no hay evaluaciones": sí existen, están en proceso de
    // validación interna y el cliente no debe verlas hasta publicarse.
    return (
      <p className="text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-sm px-3 py-2">
        Las evaluaciones aparecerán aquí cuando SOEM las publique.
      </p>
    );
  }
  return (
    <ul className="space-y-1.5">
      {evaluations.map(ev => (
        <EvaluationRow key={ev.id} evaluation={ev} onOpen={onOpen} />
      ))}
    </ul>
  );
}

function EventBlock({ event, evaluations, onOpen }) {
  const date = formatDate(event.dates?.event_start);
  return (
    <div className="px-5 py-4" data-testid={`jerarquia-evento-${event.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <p className="text-sm font-bold text-soem-navy truncate">{event.name}</p>
          <p className="text-xs text-slate-400 mt-0.5 flex items-center flex-wrap gap-x-2 gap-y-0.5">
            <span className="inline-flex items-center gap-1">
              <CalendarDays size={12} /> {date || 'Fecha por confirmar'}
            </span>
            {event.location_name && (
              <span className="inline-flex items-center gap-1">
                <MapPin size={12} /> {event.location_name}
              </span>
            )}
          </p>
        </div>
        {event.event_code && (
          <span className="text-xs font-mono text-slate-300 flex-shrink-0">{event.event_code}</span>
        )}
      </div>
      <EvaluationList evaluations={evaluations} onOpen={onOpen} />
    </div>
  );
}

function ProjectCard({ project, events, evaluations, onOpen }) {
  const eventIds = new Set(events.map(e => e.id));
  const evalsByEvent = id => evaluations.filter(ev => ev.event_id === id);
  // Evals publicadas que no cuelgan de ningún evento visible (sin
  // event_id, o de un evento que el listado del cliente no trajo): se
  // muestran igual para no esconderle al cliente información ya publicada.
  const looseEvaluations = evaluations.filter(ev => !ev.event_id || !eventIds.has(ev.event_id));

  return (
    <article className="soem-card overflow-hidden" data-testid={`jerarquia-proyecto-${project.id}`}>
      <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-bold text-soem-navy flex items-center gap-2">
            <FolderOpen size={16} className="text-soem-primary flex-shrink-0" />
            <span className="truncate">{project.name}</span>
          </h3>
          {project.description && (
            <p className="text-xs text-slate-400 mt-1 truncate">{project.description}</p>
          )}
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-xs font-semibold text-slate-500">{plural(events.length, 'evento', 'eventos')}</p>
          <p className="text-xs text-slate-400 mt-0.5">
            {plural(evaluations.length, 'evaluación publicada', 'evaluaciones publicadas')}
          </p>
        </div>
      </div>

      <div className="divide-y divide-slate-50">
        {events.length === 0 ? (
          <p className="px-5 py-4 text-sm text-slate-500">
            Todavía no hay eventos registrados en este proyecto.
          </p>
        ) : (
          events.map(evt => (
            <EventBlock key={evt.id} event={evt} evaluations={evalsByEvent(evt.id)} onOpen={onOpen} />
          ))
        )}

        {looseEvaluations.length > 0 && (
          <div className="px-5 py-4" data-testid={`jerarquia-sin-evento-${project.id}`}>
            <p className="text-sm font-bold text-soem-navy mb-2">Evaluaciones del proyecto</p>
            <p className="text-xs text-slate-400 mb-2">No están ligadas a un evento en particular.</p>
            <EvaluationList evaluations={looseEvaluations} onOpen={onOpen} />
          </div>
        )}
      </div>
    </article>
  );
}

export default function ProjectHierarchy({ projects, events, evaluations, onOpenEvaluation }) {
  // GET /projects excluye archivados y GET /events no: un evento cuyo
  // proyecto ya se archivó vendría en el listado (y en el contador de la
  // tarjeta de stats) pero no colgaría de ninguna tarjeta. Se agrupa
  // aparte para que lo que se cuenta arriba sea lo que se ve abajo.
  const projectIds = new Set(projects.map(p => p.id));
  const orphanEvents = events.filter(e => !e.project_id || !projectIds.has(e.project_id));

  if (projects.length === 0 && orphanEvents.length === 0) {
    return (
      <div className="soem-card p-10 text-center" data-testid="jerarquia-sin-proyectos">
        <Building2 size={36} className="text-slate-300 mx-auto mb-3" />
        <p className="text-slate-600 font-semibold">Todavía no tienes proyectos asignados.</p>
        <p className="text-slate-500 text-sm mt-1">
          Escríbele a tu contacto en SOEM para que vinculemos tus proyectos y eventos a esta cuenta.
        </p>
      </div>
    );
  }

  return (
    <section className="space-y-4" data-testid="cliente-jerarquia">
      <div>
        <h2 className="text-lg font-bold text-soem-navy">Mis Proyectos</h2>
        <p className="text-slate-500 text-sm mt-0.5">
          Tus proyectos con sus eventos y las evaluaciones que SOEM ya publicó.
        </p>
      </div>
      <div className="space-y-4">
        {projects.map(p => (
          <ProjectCard
            key={p.id}
            project={p}
            events={events.filter(e => e.project_id === p.id)}
            evaluations={evaluations.filter(ev => ev.project_id === p.id)}
            onOpen={onOpenEvaluation}
          />
        ))}

        {orphanEvents.length > 0 && (
          <article className="soem-card overflow-hidden" data-testid="jerarquia-otros-eventos">
            <div className="px-5 py-4 border-b border-slate-100">
              <h3 className="font-bold text-soem-navy flex items-center gap-2">
                <FolderOpen size={16} className="text-slate-400 flex-shrink-0" />
                <span>Otros eventos</span>
              </h3>
              <p className="text-xs text-slate-400 mt-1">
                Eventos tuyos cuyo proyecto ya no está activo en la plataforma.
              </p>
            </div>
            <div className="divide-y divide-slate-50">
              {orphanEvents.map(evt => (
                <EventBlock
                  key={evt.id}
                  event={evt}
                  evaluations={evaluations.filter(ev => ev.event_id === evt.id)}
                  onOpen={onOpenEvaluation}
                />
              ))}
            </div>
          </article>
        )}
      </div>
    </section>
  );
}
